import { STEP_KEY } from 'pages/funnel/utils';

import { COUNTRIES } from '../../common/constants';

import { SPECIALTY_FLOW } from './specialtyFlow/config';
import { SpecialtyFlow } from './specialtyFlow/specialtyFlow';
import { FUNNEL_STEPS } from './stepConfiguration';

// Default funnel configuration
export const DEFAULT_CONFIGURATION = {
  id: 1,
  order: [
    FUNNEL_STEPS.Welcome,
    FUNNEL_STEPS.Location,
    FUNNEL_STEPS.Reason,
    FUNNEL_STEPS.ReasonDetails,
    FUNNEL_STEPS.Attitude,
    FUNNEL_STEPS.Goals,
    FUNNEL_STEPS.Criteria,
    FUNNEL_STEPS.LastVisited,
    FUNNEL_STEPS.Anxiety,
    FUNNEL_STEPS.Availability,
    FUNNEL_STEPS.HowSoon,
    FUNNEL_STEPS.Insurance,
    FUNNEL_STEPS.Contact,
  ],
};

// Root config is an experiment config
const SPECIALTY_FLOW_ROOT_CONFIG = {
  id: SPECIALTY_FLOW.CONFIG_ID,
  branchName: SPECIALTY_FLOW.BRANCHES.ROOT,
  markets: SPECIALTY_FLOW.MARKETS,
  trafficSplit: 0.5,
  active: ({ storage }) => !storage.get(STEP_KEY.REASON),
  order: SpecialtyFlow.generateOrder(SPECIALTY_FLOW.BRANCHES.ROOT),
};

// Branch configs are control configs that are only active if that branch is requested
export const SPECIALTY_FLOW_BRANCH_CONFIGS = Object.values(
  SPECIALTY_FLOW.BRANCHES,
)
  .filter(branch => branch !== SPECIALTY_FLOW.BRANCHES.ROOT)
  .map(branchName => SpecialtyFlow.generateBranchConfig(branchName));

/*
  *Segments:*

  Segments are OR statements. If the user's location matches any of the segments given, the config is valid for them.
  E.g. if a config is country: Canada, and state: Ontario, it will match any user in Canada, or any user in Ontario.
    So a user in Calgary will be matched by the Canada segment, even though their province doesn't match.

  Segment fields can be country, state, and/or locality. Largest area takes precedence (country > state > locality).
  Country field should be from the COUNTRIES object, other fields should be arrays of strings -- all lowercase.

  ```
  segment: {
    country: COUNTRIES.CANADA,
    state: ['on'],
    locality: ['toronto'],
  },
  ```

  There are no overlaps in country or state codes, but cities could be the same, so be aware of that.

  *Markets:*

  Markets take precedence over segment (since it accounts for more specific location data like zip codes).
  Markets are arrays of strings -- all lowercase.

  ```
  id: 999,
  markets: ['sacramento', 'seattle'],
  ```
*/

// Configurations used for per-region control variants
// We always fall back to the default configuration if no region-specific configuration is found
// Note: segment is required!
export const CONTROL_CONFIGURATIONS = [
  {
    id: 1005,
    active: true,
    segment: {
      country: COUNTRIES.CANADA,
    },
    order: [
      FUNNEL_STEPS.Welcome,
      FUNNEL_STEPS.Location,
      FUNNEL_STEPS.OngoingCare,
      FUNNEL_STEPS.ConcernsTreatment,
      FUNNEL_STEPS.Attitude,
      FUNNEL_STEPS.Goals,
      FUNNEL_STEPS.Criteria,
      FUNNEL_STEPS.LastVisited,
      FUNNEL_STEPS.Anxiety,
      FUNNEL_STEPS.Availability,
      FUNNEL_STEPS.HowSoon,
      FUNNEL_STEPS.Insurance,
      FUNNEL_STEPS.Contact,
    ],
  },
  {
    // https://opencare-group.monday.com/boards/5950393442/pulses/6891767055
    // This is a dynamic funnel where the steps order is based on the emergency selection.
    // Keep its id as the default one for consistency.
    id: 1,
    subId: 1017,
    active: ({ storage }) => storage.get(STEP_KEY.REASON)?.isUrgent === 'No',
    segment: {
      country: COUNTRIES.UNITED_STATES,
    },
    order: [
      FUNNEL_STEPS.Welcome,
      FUNNEL_STEPS.Location,
      FUNNEL_STEPS.Reason,
      FUNNEL_STEPS.Attitude,
      FUNNEL_STEPS.LastVisited,
      FUNNEL_STEPS.Anxiety,
      FUNNEL_STEPS.Goals,
      FUNNEL_STEPS.ReasonDetails,
      FUNNEL_STEPS.Availability,
      FUNNEL_STEPS.HowSoon,
      FUNNEL_STEPS.Criteria,
      FUNNEL_STEPS.Insurance,
      FUNNEL_STEPS.Contact,
    ],
  },
  ...SPECIALTY_FLOW_BRANCH_CONFIGS,
];

// Configurations used for funnel experiments, make sure id is > 100
export const ALTERNATE_CONFIGURATIONS = [
  {
    id: 2,
    active: true,
    order: [
      FUNNEL_STEPS.Welcome,
      FUNNEL_STEPS.Location,
      FUNNEL_STEPS.Reason,
      FUNNEL_STEPS.ReasonDetails,
      FUNNEL_STEPS.Insurance,
    ],
  },
  {
    id: 3,
    active: true,
    order: [
      FUNNEL_STEPS.Location,
      FUNNEL_STEPS.Reason,
      FUNNEL_STEPS.ReasonDetails,
      FUNNEL_STEPS.Insurance,
    ],
  },
  {
    id: 1001, // DKI Insurance Configuration - Marketing Insurance Experiment on find-dentist/ LP
    active: true,
    order: [
      FUNNEL_STEPS.Welcome,
      FUNNEL_STEPS.Location,
      FUNNEL_STEPS.Reason,
      FUNNEL_STEPS.ReasonDetails,
      FUNNEL_STEPS.Attitude,
      FUNNEL_STEPS.Goals,
      FUNNEL_STEPS.Criteria,
      FUNNEL_STEPS.LastVisited,
      FUNNEL_STEPS.Anxiety,
      FUNNEL_STEPS.Availability,
      FUNNEL_STEPS.HowSoon,
      FUNNEL_STEPS.Contact,
    ],
  },
  {
    id: 1002, // Patient Lead Modal Configuration
    active: true,
    order: [
      FUNNEL_STEPS.Welcome,
      FUNNEL_STEPS.Location,
      FUNNEL_STEPS.Reason,
      FUNNEL_STEPS.ReasonDetails,
      FUNNEL_STEPS.Attitude,
      FUNNEL_STEPS.Goals,
      FUNNEL_STEPS.Criteria,
      FUNNEL_STEPS.LastVisited,
      FUNNEL_STEPS.Anxiety,
      FUNNEL_STEPS.Availability,
      FUNNEL_STEPS.HowSoon,
      FUNNEL_STEPS.Insurance,
    ],
  },
];

// Configurations used for funnel experiments, make sure id is DEFAULT_CONFIGURATION.id + 1
// If we want to full time the experiment, update the DEFAULT_CONFIGURATION along it's id
export const TEST_CONFIGURATIONS = [
  // NOTES:
  // * 'trafficSplit' must be a value between 0 and 1. ie 0.5 = 50%
  // * If 'segment' is not provided, it will include users from everywhere
  // Use DEFAULT_CONFIGURATION.order.filter(...) for step-removal experiments
  // EXAMPLE CONFIGURATION FOR QUICK USE.
  // {
  //   id: 101,
  //   active: true,
  //   trafficSplit: 0.5,
  //   segment: {
  //     country: COUNTRIES.CANADA,
  //     state: ['on'],
  //     locality: ['toronto'],
  //   },
  //   order: [ ...ADD STEPS HERE ],
  // },
  SPECIALTY_FLOW_ROOT_CONFIG,
];

export const ALL_CONFIGURATIONS = [
  DEFAULT_CONFIGURATION,
  ...CONTROL_CONFIGURATIONS,
  ...ALTERNATE_CONFIGURATIONS,
  ...TEST_CONFIGURATIONS,
];
